import {useEffect, useMemo} from 'react';
import {useSearchParams} from 'react-router-dom';
import {useAuth} from '@common/auth/use-auth';
import {useSettings} from '@common/core/settings/use-settings';
import {useBootstrapData} from '@common/core/bootstrap-data/bootstrap-data-context';
import {
  isTrialOfferGoneError,
  useValidateTrialCode,
} from '@common/billing/trials/trial-requests';
import {
  clearPendingTrial,
  getPendingTrial,
  savePendingTrial,
  usePendingTrial,
} from '@common/billing/trials/pending-trial';
import {isTrialSubscriptionRow} from '@common/billing/trials/is-trial-subscription';
import {TrialOffer} from './trial-offer-store';

/** Query parameter that attaches a trial code to any page: /artist/1?trial=X */
export const TRIAL_LINK_PARAM = 'trial';

const MAX_CODE_LENGTH = 64;

// The offer is asked about on every page a visitor with a code opens, and the
// validate endpoint is limited per IP address (shared by many phones on one
// mobile network): one answer is reused for ten minutes.
const OFFER_STALE_TIME = 10 * 60 * 1000;

/**
 * Remembers a trial code that arrives on any page as ?trial=CODE, together
 * with the ad it came from, the same way the /try page does for ?code=.
 */
export function useCaptureTrialLink() {
  const [searchParams] = useSearchParams();
  const code = (searchParams.get(TRIAL_LINK_PARAM) || '').trim();

  useEffect(() => {
    if (code && code.length <= MAX_CODE_LENGTH) {
      savePendingTrial(code, searchParams);
    }
    // only on a new code - later search param changes carry no new attribution
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);
}

/**
 * The free trial this visitor can start right now, or null.
 *
 * Null for everyone the offer is not for: no remembered code, the feature
 * switched off, a subscriber, someone already on a trial or who has had one,
 * a code that is not usable (it is then forgotten), and deployments that send
 * every visitor to an operator page.
 */
export function useTrialOffer(): TrialOffer | null {
  const {billing} = useSettings();
  const {
    data: {environment},
  } = useBootstrapData();
  const {user, isSubscribed, isOnTrial} = useAuth();
  const pending = usePendingTrial();

  const enabled = !!billing?.enable && !!billing?.trial_codes?.enable;
  const operatorFunnel = environment?.DEFAULT_REDIRECT_GATEWAY === 'zainSD';
  const hasUsedTrial = !!user?.subscriptions?.some(isTrialSubscriptionRow);
  const eligible =
    enabled &&
    !operatorFunnel &&
    !!pending &&
    !isSubscribed &&
    !isOnTrial &&
    !hasUsedTrial;

  const code = eligible ? pending!.code : null;
  const query = useValidateTrialCode(code, {staleTime: OFFER_STALE_TIME});
  const data = query.data;
  const isGone = (!!data && !data.valid) || isTrialOfferGoneError(query.error);

  // a code that is definitely not usable should not follow the visitor around
  useEffect(() => {
    if (code && isGone && getPendingTrial()?.code === code) {
      clearPendingTrial();
    }
  }, [code, isGone]);

  const isValid = !!code && !!data?.valid;
  const offerCode = data?.code || code || '';
  const trialDays = data?.trial_days || 0;
  const phoneVerificationRequired = data?.phone_verification_required !== false;
  const countries = (data?.countries ?? []).join(',');
  const phoneRegions = (data?.phone_regions ?? []).join(',');
  const entryFlow = data?.entry_flow === 'phone' ? 'phone' : 'offer';

  return useMemo(() => {
    if (!isValid || !offerCode || trialDays < 1) {
      return null;
    }
    return {
      code: offerCode,
      trialDays,
      phoneVerificationRequired,
      countries: countries ? countries.split(',') : [],
      phoneRegions: phoneRegions ? phoneRegions.split(',') : [],
      entryFlow,
    };
  }, [
    isValid,
    offerCode,
    trialDays,
    phoneVerificationRequired,
    countries,
    phoneRegions,
    entryFlow,
  ]);
}
